import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { setApplicationFlowFormErrors } from "@krea/customer-features/services/formService";
import { FORM_STEP } from "@krea/customer-features/shared-components/application-form";
import { useBankIDStore } from "@krea/customer-features/store/bankID/hooks";
import { useLoanApplicationFormStore } from "@krea/customer-features/store/loan-application-form/hooks";
import { useModalStore } from "@krea/customer-features/store/modal/hooks";
import { COLLECT_LOAN_APPLICATION_STORE_KEY } from "@krea/customer-features/store/queryClient";
import {
  BANKID_FLOW_STATES,
  HINT_CODE_STATES,
} from "@krea/customer-features/utils";

import { collectApplicationViaBankID } from "store/loan-application-form/api";

export const useCollectQuery = () => {
  const navigate = useNavigate();
  const closeModal = useModalStore(({ closeModal }) => closeModal);
  const resetFormState = useLoanApplicationFormStore(
    ({ resetFormState }) => resetFormState,
  );
  const setCurrentStep = useLoanApplicationFormStore(
    ({ setCurrentStep }) => setCurrentStep,
  );

  const { orderRef, resetBankIDStore } = useBankIDStore((state) => ({
    orderRef: state.orderRef,
    resetBankIDStore: state.resetBankIDStore,
  }));

  return useQuery({
    queryKey: [COLLECT_LOAN_APPLICATION_STORE_KEY],
    queryFn: async () => {
      try {
        const { collectPendingLoanApplication: data } =
          await collectApplicationViaBankID(orderRef);

        const { status, hintCode, loanApplication } = data || {};

        if (status === BANKID_FLOW_STATES.FAILED) {
          resetBankIDStore();

          return { status, hintCode };
        } else if (status === BANKID_FLOW_STATES.PENDING) {
          return { status, hintCode };
        }

        // Complete
        closeModal();
        resetFormState();
        resetBankIDStore();

        navigate(`/loan-applications/${loanApplication.id}`);

        return { status, hintCode };
      } catch (err) {
        const hasFormErrors = setApplicationFlowFormErrors(err);
        resetBankIDStore();

        if (hasFormErrors) {
          setCurrentStep(FORM_STEP.BASIC_DETAILS);
        }
        console.error("Error posting form", err);

        return {
          status: BANKID_FLOW_STATES.FAILED,
          hintCode: HINT_CODE_STATES.ERROR,
          errorCode: err?.messages?.[0]?.code,
        };
      }
    },
    refetchInterval: 2000,
    refetchIntervalInBackground: true,
    staleTime: Infinity,
    enabled: !!orderRef,
  });
};
